import { useCallback, useEffect, useRef, useState } from "react";
import { api, errMsg } from "@/lib/api";

const CONCURRENCY = 3;
const DONE = new Set(["imported", "duplicate", "failed"]);

export async function putChunk(uploadId, blob, offset, onProgress) {
  for (let attempt = 0; ; attempt += 1) {
    try {
      await api.put(`/uploads/${uploadId}`, blob, {
        params: { offset },
        headers: { "Content-Type": "application/octet-stream" },
        onUploadProgress: (e) => onProgress(e.loaded),
      });
      return;
    } catch (e) {
      if (attempt >= 3 || (e.response && e.response.status < 500 && e.response.status !== 409)) throw e;
      await new Promise((r) => setTimeout(r, 1000 * (attempt + 1)));
    }
  }
}

export function useUploader(onBatchStored) {
  const [entries, setEntries] = useState([]);
  const queue = useRef([]);
  const active = useRef(0);
  const patch = useCallback((key, p) => setEntries((prev) => prev.map((e) => (e.key === key ? { ...e, ...p } : e))), []);

  const uploadOne = useCallback(async (entry) => {
    const { file, key, space, albumId } = entry;
    try {
      patch(key, { state: "uploading" });
      const { data } = await api.post("/uploads/init", { filename: file.name, size: file.size, space, album_id: albumId || null, last_modified: file.lastModified });
      let offset = 0;
      while (offset < file.size) {
        const blob = file.slice(offset, offset + data.chunk_size);
        const base = offset;
        await putChunk(data.upload_id, blob, offset, (loaded) => patch(key, { progress: Math.min(99, ((base + loaded) / file.size) * 100) }));
        offset += blob.size;
      }
      const item = (await api.post(`/uploads/${data.upload_id}/complete`)).data;
      patch(key, { state: "processing", progress: 100, itemId: item.id });
    } catch (e) {
      patch(key, { state: "failed", error: errMsg(e) });
    }
  }, [patch]);

  const pump = useCallback(() => {
    while (active.current < CONCURRENCY && queue.current.length) {
      const next = queue.current.shift();
      active.current += 1;
      uploadOne(next).finally(() => {
        active.current -= 1;
        if (!queue.current.length && !active.current) onBatchStored?.();
        pump();
      });
    }
  }, [uploadOne, onBatchStored]);

  const add = useCallback((files, space, albumId = null, group = null) => {
    const fresh = Array.from(files).map((file, i) => ({
      key: `${Date.now()}-${group || ""}-${i}-${file.name}`, file, name: file.name, space, albumId, group, progress: 0, state: "queued", error: null, itemId: null,
    }));
    setEntries((prev) => [...fresh, ...prev]);
    queue.current.push(...fresh);
    pump();
  }, [pump]);

  const processingIds = entries.filter((e) => e.state === "processing").map((e) => e.itemId).join(",");
  useEffect(() => {
    if (!processingIds) return undefined;
    const t = setInterval(async () => {
      const { data } = await api.post("/uploads/status", { item_ids: processingIds.split(",") }).catch(() => ({ data: { items: [] } }));
      const byId = Object.fromEntries(data.items.map((s) => [s.id, s]));
      let changed = false;
      setEntries((prev) => prev.map((e) => {
        const s = e.state === "processing" && byId[e.itemId];
        if (!s || s.status === "queued" || s.status === "processing") return e;
        changed = true;
        return { ...e, state: s.status === "error" ? "failed" : s.duplicate ? "duplicate" : "imported", error: s.error, thumb: s.thumb_url };
      }));
      if (changed) onBatchStored?.();
    }, 3000);
    return () => clearInterval(t);
  }, [processingIds, onBatchStored]);

  const retry = useCallback((key) => {
    setEntries((prev) => prev.map((e) => {
      if (e.key !== key || e.state !== "failed") return e;
      const again = { ...e, state: "queued", progress: 0, error: null };
      queue.current.push(again);
      return again;
    }));
    setTimeout(pump, 0);
  }, [pump]);
  const retryFailed = useCallback(() => {
    setEntries((prev) => prev.map((e) => {
      if (e.state !== "failed") return e;
      const again = { ...e, state: "queued", progress: 0, error: null };
      queue.current.push(again);
      return again;
    }));
    setTimeout(pump, 0);
  }, [pump]);
  const clearFinished = useCallback(() => setEntries((prev) => prev.filter((e) => !DONE.has(e.state))), []);
  const summary = entries.reduce((acc, e) => ({ ...acc, [e.state]: (acc[e.state] || 0) + 1 }), {});
  return { entries, add, retry, retryFailed, summary, clearFinished };
}
